import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Xmark } from '@gravity-ui/icons';
import { Button } from '@heroui/react';
import { useViewerStore } from '@stores/viewerStore';
import { createShortLink } from '@services/share';
import { copyText } from '@utils/clipboard';
import { InviteList } from './InviteList';
import { PublishControl } from './PublishControl';

type CopyState = 'idle' | 'copied' | 'failed' | 'permission-denied';

/** 创作者分享面板：场景卡片 + 短链 + 邀请管理 + 发布控制。 */
export function CreatorShareCard() {
  const { t } = useTranslation();
  const sceneConfig = useViewerStore((s) => s.sceneConfig);
  const sceneId = useViewerStore((s) => s.sceneId);
  const shortUrl = useViewerStore((s) => s.shortUrl);
  const setShortUrl = useViewerStore((s) => s.setShortUrl);
  const setOpen = useViewerStore((s) => s.setSharePanelOpen);
  const [copyState, setCopyState] = useState<CopyState>('idle');

  // 打开时请求短链
  useEffect(() => {
    if (!sceneId || shortUrl) return;
    void createShortLink(sceneId).then((res) => setShortUrl(res.shortUrl));
  }, [sceneId, shortUrl, setShortUrl]);

  useEffect(() => {
    if (copyState === 'idle') return;
    const timer = window.setTimeout(() => setCopyState('idle'), 1800);
    return () => window.clearTimeout(timer);
  }, [copyState]);

  const displayUrl = shortUrl || window.location.href;

  function handleCopy() {
    void copyText(displayUrl)
      .then((result) =>
        setCopyState(
          result.ok
            ? 'copied'
            : result.reason === 'permission-denied'
              ? 'permission-denied'
              : 'failed',
        ),
      )
      .catch(() => setCopyState('failed'));
  }

  return (
    <>
      <button
        type="button"
        className="absolute inset-0 z-40 cursor-default appearance-none border-none bg-transparent p-0"
        aria-label="Close share panel"
        onClick={() => setOpen(false)}
      />
      <div
        className="absolute right-14 bottom-4 z-50 w-[26rem] max-w-[calc(100vw-7rem)] overflow-y-auto rounded-2xl bg-white/10 p-5 shadow-xl backdrop-blur-md"
        style={{ maxHeight: 'calc(100vh - 8rem)' }}
      >
        {/* Header */}
        <div className="mb-4 flex items-start justify-between">
          <h3 className="text-base font-semibold text-white">Share Scene</h3>
          <Button
            isIconOnly
            size="md"
            onPress={() => setOpen(false)}
            aria-label="Close share panel"
            className="ml-2 flex-shrink-0 rounded-full text-white/40 hover:text-white"
          >
            <Xmark width={14} height={14} />
          </Button>
        </div>

        {/* 场景卡片 */}
        {sceneConfig && (
          <div className="mb-4 rounded-xl border border-white/10 bg-white/10 p-3">
            <p className="truncate text-sm font-medium text-white">{sceneConfig.name}</p>
            {sceneConfig.creator && (
              <p className="mt-1 truncate text-xs text-white/50">By {sceneConfig.creator.name}</p>
            )}
          </div>
        )}

        {/* 短链 */}
        <div className="mb-1 text-xs font-medium text-white/70">Share link</div>
        <div className="rounded-xl bg-black/20 p-3 text-xs break-all text-white/70">
          {displayUrl}
        </div>
        <Button
          fullWidth
          size="md"
          onPress={handleCopy}
          className="mt-3 rounded-full bg-white text-base font-medium text-[#18181B]"
        >
          Copy link
        </Button>
        {copyState !== 'idle' && (
          <p className="mt-2 text-center text-xs text-white/60">
            {copyState === 'copied'
              ? t('share.link-copied')
              : copyState === 'permission-denied'
                ? t('share.copy-permission-denied')
                : t('share.copy-failed')}
          </p>
        )}

        {/* 邀请管理 */}
        <div className="mt-5">
          <InviteList />
        </div>

        {/* 发布控制 */}
        <div className="mt-5">
          <PublishControl />
        </div>
      </div>
    </>
  );
}
